// Public-side mirror of <DrawReveal>. Same source of truth (the draws doc
// with `revealedAt` set), but calmer motion appropriate to a phone screen.
// Pops a centred card over the pitch area for ~5 seconds, then dismisses
// itself.
//
// Critical constraint: ships in the eager /live bundle, so no lucide-react
// and no motion/react imports here. Pure CSS animations only.

import { useEffect } from "react";
import type { DrawDoc, WithId, ZoneDoc } from "../../types";

interface Props {
  draw: WithId<DrawDoc>;
  zone: WithId<ZoneDoc> | undefined;
  onDone: () => void;
  durationMs?: number;
}

export function LiveDrawCelebration({
  draw, zone, onDone, durationMs = 5000,
}: Props): JSX.Element {
  // Auto-dismiss after duration. One timer per mount; cleaned up on unmount
  // so a rapid succession of draws can't stack expired timers.
  useEffect(() => {
    const id = window.setTimeout(onDone, durationMs);
    return () => window.clearTimeout(id);
  }, [onDone, durationMs]);

  return (
    <div
      // Centred over the pitch area only — parent positions absolute inset-0.
      className="absolute inset-0 z-20 grid place-items-center p-4 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div
        // Soft backdrop blur ONLY behind the card — preserves the live grid
        // visibility around the edges so guests still see their zones
        // filling underneath.
        className="absolute inset-0 bg-bg/40 backdrop-blur-sm animate-fade-up"
        aria-hidden
      />
      <div
        className="relative w-full max-w-sm bg-bg-elev/95 border border-accent/40 rounded-2xl px-6 py-7 shadow-2xl animate-fade-up text-center"
      >
        <div className="text-[11px] uppercase tracking-[0.3em] text-accent mb-2">
          {draw.label}
        </div>
        <div className="text-xs uppercase tracking-widest text-fg-muted mb-3">Winner</div>
        <div className="font-display text-6xl text-accent mb-3 tabular-nums leading-none">
          {zone?.label ?? "?"}
        </div>
        <div className="font-display text-2xl text-fg mb-1 truncate">
          {draw.winnerName ?? "—"}
        </div>
        {draw.bookingRef && (
          <div className="text-xs text-fg-muted font-mono tracking-widest mt-2">
            {draw.bookingRef}
          </div>
        )}
      </div>
    </div>
  );
}
